// Client-side port of the backend LengthParser (com.timberbase.app.util.LengthParser).
// The database stores every length in INCHES (NUMERIC(6,3)); this converts a free-form length
// string to inches (3 decimals) so we can validate it against a product's available lengths.
// Precedence follows the backend: inch markers, "A" suffix, fractions, feet, mm, then a bare
// number defaults to FEET. Only the client reads a trailing feet mark ("12'"); the backend
// rejects it.

const MM_TO_INCHES = 0.0393701;

/** Round to 3 decimals (matches NUMERIC(6,3) + RoundingMode.HALF_UP). */
export function round3(value: number): number {
  // Round the magnitude and reapply the sign so ties break AWAY from zero for negatives too,
  // matching the backend's BigDecimal.setScale(3, HALF_UP) (JS Math.round breaks ties toward +∞).
  // Pre-round to 9dp first to absorb binary floating-point error (e.g. 196.85049999998 ->
  // 196.8505) so HALF_UP at 3dp lands on the same boundary.
  const absorbed = Math.round(Math.abs(value) * 1e9) / 1e9;
  const rounded = Math.round((absorbed + Number.EPSILON) * 1000) / 1000;
  return Math.sign(value) * rounded;
}

function toNumber(raw: string): number | null {
  const cleaned = raw.replace(/,/g, '').trim();
  // Mirror the backend's `new BigDecimal(...)`: accept an optional sign and a leading/trailing dot
  // (".5", "5.", "+8", "-3"), not just unsigned "d.d".
  if (cleaned === '' || !/^[+-]?(\d+\.?\d*|\.\d+)$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parse a length string to decimal inches, or null if unparseable.
 *  "8" -> 96, "108\"" -> 108, "96 1/4" -> 96.25, "16 ft" -> 192, "1000 mm" -> 39.37, "108A" -> 108
 */
export function parseLengthToInches(input: string): number | null {
  if (!input || !input.trim()) return null;
  const s = input.trim().toUpperCase();

  // Inch markers (highest precedence): trailing ", ”, " in", " inch", " inches"
  // (mirrors the backend, which strips INCHES, INCH, then IN).
  if (
    s.endsWith('"') ||
    s.endsWith('”') ||
    /\sINCH(?:ES)?$/.test(s) ||
    /\sIN$/.test(s)
  ) {
    const n = toNumber(
      s
        .replace(/["”]/g, '')
        .replace(/\sINCH(?:ES)?$/, '')
        .replace(/\sIN$/, '')
    );
    return n === null ? null : round3(n);
  }

  // "A" (actual length) suffix -> inches. The backend strips a trailing "A" regardless of a space
  // ("108 A"), so allow optional whitespace before it.
  if (/\d\s*A$/.test(s)) {
    const n = toNumber(s.slice(0, -1));
    return n === null ? null : round3(n);
  }

  // Fractional inches: "96 1/4".
  const fraction = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (fraction) {
    const whole = Number(fraction[1]);
    const num = Number(fraction[2]);
    const den = Number(fraction[3]);
    if (den === 0) return null;
    return round3(whole + num / den);
  }

  // Feet suffix: "16 ft", "8 feet", "8ft" (the backend uses endsWith and doesn't require a space).
  if (/\s*(FT|FEET)$/.test(s)) {
    const n = toNumber(s.replace(/\s*(FT|FEET)$/, ''));
    return n === null ? null : round3(n * 12);
  }

  // Millimetres.
  if (/(MM|MILLIMETERS?)$/.test(s)) {
    const n = toNumber(s.replace(/\s*(MM|MILLIMETERS?)$/, ''));
    return n === null ? null : round3(n * MM_TO_INCHES);
  }

  // Feet mark: "12'" — the form the catalog's own length labels use, typed straight or curly.
  if (/['′’]$/.test(s)) {
    const n = toNumber(s.replace(/\s*['′’]$/, ''));
    return n === null ? null : round3(n * 12);
  }

  // Default: a bare number is feet.
  const bare = toNumber(s);
  return bare === null ? null : round3(bare * 12);
}

/** True when two inch values match at the DB's 3-decimal precision. */
export function inchesEqual(a: number, b: number): boolean {
  return round3(a) === round3(b);
}

/**
 * True when the input is a plain number with no unit (e.g. "8", "96", "1,000", "8.5") — the case
 * `parseLengthToInches` silently defaults to feet. Used to warn that the unit was assumed. Any unit
 * marker (", in, ft, mm, an "A" suffix, or a fraction) makes this false.
 */
export function isBareNumberLength(input: string): boolean {
  const cleaned = (input ?? '').replace(/,/g, '').trim();
  // Match toNumber's grammar exactly so every value parseLengthToInches treats as a bare (unit-less)
  // number triggers the "assumed feet" warning — including a sign or a leading/trailing dot
  // (".5", "5.", "+8", "-3").
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(cleaned);
}
